import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Response } from '../../../../_shared/models';
import { PaginatedResult } from '../../../../_shared/models/paginated-result.model';
import { AddStockRequest } from '../models/add-stock-request.model';
import { CreatePartRequest } from '../models/create-part-request.model';
import { InventoryOverviewPaginatedRequest, PartInventoryItem } from '../models/inventory-overview.model';
import { PartsUsageForecastItem, PartsUsageForecastRequest } from '../models/parts-usage-forecast.model';
import { PartDetailsOverview } from '../models/part-details-overview.model';
import { PartDetails } from '../models/part-details.model';
import { ExistingPartNumber, UploadPartsExcelRequest } from '../models/part-import.model';
import { PartsKpiRequest, PartsKpiResponse } from '../models/parts-kpi.model';
import { PartSupplier } from '../models/part-supplier.model';
import { PartUsageByPartIdItem, PartUsageByPartIdRequest } from '../models/part-usage-by-part-id.model';
import { ReceivedPartHistoryItem, ReceivedPartHistoryRequest } from '../models/received-part-history.model';
import { ReturnPartRequest } from '../models/return-part-request.model';
import { UpdatePartRequest } from '../models/update-part-request.model';
import { UpdateReorderPointNotificationRequest } from '../models/update-reorder-point-notification-request.model';
import { WorkOrderAssignedTechnician } from '../models/work-order-assigned-technician.model';
import { WorkOrderIssuedPart } from '../models/work-order-issued-part.model';

@Injectable({
  providedIn: 'root'
})
export class PartsAndInventoryService {
  private http = inject(HttpClient);

  createPartAsync(request: CreatePartRequest): Promise<Response<void>> {
    return firstValueFrom(
      this.http.post<Response<void>>(`${environment.apiEndpoint}/parts/create`, request)
    );
  }

  bulkUploadPartsAsync(request: UploadPartsExcelRequest, cancellation: Subject<void>): Promise<Response<string[]>> {
    return firstValueFrom(
      this.http.post<Response<string[]>>(`${environment.apiEndpoint}/parts/bulk-upload-parts`, request)
        .pipe(takeUntil(cancellation))
    );
  }

  /** Returns, for the given OEM numbers, every (OEM number, warehouse) pair already in use in the user's accounts. */
  getExistingPartNumbersAsync(partNumbers: string[], cancellation: Subject<void>): Promise<Response<ExistingPartNumber[]>> {
    return firstValueFrom(
      this.http.post<Response<ExistingPartNumber[]>>(`${environment.apiEndpoint}/parts/existing-part-numbers`, { partNumbers })
        .pipe(takeUntil(cancellation))
    );
  }

  getPartByIdAsync(partId: number): Promise<Response<PartDetails>> {
    return firstValueFrom(
      this.http.get<Response<PartDetails>>(`${environment.apiEndpoint}/parts/${partId}`)
    );
  }

  updatePartAsync(partId: number, request: UpdatePartRequest): Promise<Response<void>> {
    return firstValueFrom(
      this.http.put<Response<void>>(`${environment.apiEndpoint}/parts/${partId}`, request)
    );
  }

  deletePartByIdAsync(partId: number): Promise<Response<void>> {
    return firstValueFrom(
      this.http.delete<Response<void>>(`${environment.apiEndpoint}/parts/${partId}`)
    );
  }

  addPartStockAsync(request: AddStockRequest): Promise<Response<void>> {
    return firstValueFrom(
      this.http.post<Response<void>>(`${environment.apiEndpoint}/parts/add-stock`, request)
    );
  }

  returnPartAsync(request: ReturnPartRequest): Promise<Response<void>> {
    return firstValueFrom(
      this.http.post<Response<void>>(`${environment.apiEndpoint}/parts/return-part`, request)
    );
  }

  getPartSuppliersAsync(partId: number): Promise<Response<PartSupplier[]>> {
    return firstValueFrom(
      this.http.get<Response<PartSupplier[]>>(`${environment.apiEndpoint}/parts/${partId}/suppliers`)
    );
  }

  getPaginatedPartsInventoryAsync(request: InventoryOverviewPaginatedRequest): Promise<Response<PaginatedResult<PartInventoryItem>>> {
    return firstValueFrom(
      this.http.post<Response<PaginatedResult<PartInventoryItem>>>(`${environment.apiEndpoint}/parts/paginated-parts/filter`, request)
    );
  }

  getPartsUsageForecastAsync(request: PartsUsageForecastRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<PartsUsageForecastItem>>> {
    return firstValueFrom(
      this.http.post<Response<PaginatedResult<PartsUsageForecastItem>>>(`${environment.apiEndpoint}/parts/parts-usage-forecast/filter`, request).pipe(takeUntil(cancellation))
    );
  }

  getTotalPartsAsync(request: PartsKpiRequest): Promise<Response<PartsKpiResponse>> {
    return firstValueFrom(
      this.http.post<Response<PartsKpiResponse>>(`${environment.apiEndpoint}/parts/total-parts`, request)
    );
  }

  getOutOfStockPartsAsync(request: PartsKpiRequest): Promise<Response<PartsKpiResponse>> {
    return firstValueFrom(
      this.http.post<Response<PartsKpiResponse>>(`${environment.apiEndpoint}/parts/out-of-stock-parts`, request)
    );
  }

  getLowStockPartsAsync(request: PartsKpiRequest): Promise<Response<PartsKpiResponse>> {
    return firstValueFrom(
      this.http.post<Response<PartsKpiResponse>>(`${environment.apiEndpoint}/parts/low-stock-parts`, request)
    );
  }

  getInventoryValueAsync(request: PartsKpiRequest): Promise<Response<PartsKpiResponse>> {
    return firstValueFrom(
      this.http.post<Response<PartsKpiResponse>>(`${environment.apiEndpoint}/parts/inventory-value-parts`, request)
    );
  }

  getPartDetailsOverviewAsync(partId: number): Promise<Response<PartDetailsOverview>> {
    return firstValueFrom(
      this.http.get<Response<PartDetailsOverview>>(`${environment.apiEndpoint}/parts/${partId}/details`)
    );
  }

  getPartsUsageByPartIdAsync(request: PartUsageByPartIdRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<PartUsageByPartIdItem>>> {
    return firstValueFrom(
      this.http.post<Response<PaginatedResult<PartUsageByPartIdItem>>>(`${environment.apiEndpoint}/parts/part-usage/filter`, request).pipe(takeUntil(cancellation))
    );
  }

  getReceivedPartHistoryAsync(request: ReceivedPartHistoryRequest): Promise<Response<PaginatedResult<ReceivedPartHistoryItem>>> {
    return firstValueFrom(
      this.http.post<Response<PaginatedResult<ReceivedPartHistoryItem>>>(`${environment.apiEndpoint}/parts/received-history`, request)
    );
  }

  getIssuedPartsByWorkOrderAsync(workOrderId: number, cancellation: Subject<void>): Promise<Response<WorkOrderIssuedPart[]>> {
    return firstValueFrom(
      this.http.get<Response<WorkOrderIssuedPart[]>>(`${environment.apiEndpoint}/parts/work-orders/${workOrderId}/issued-parts`).pipe(takeUntil(cancellation))
    );
  }

  getWorkOrderAssignedTechniciansAsync(workOrderId: number, cancellation: Subject<void>): Promise<Response<WorkOrderAssignedTechnician[]>> {
    return firstValueFrom(
      this.http.get<Response<WorkOrderAssignedTechnician[]>>(`${environment.apiEndpoint}/parts/work-orders/${workOrderId}/technicians`).pipe(takeUntil(cancellation))
    );
  }

  updateReorderPointNotificationAsync(request: UpdateReorderPointNotificationRequest): Promise<Response<void>> {
    return firstValueFrom(
      this.http.put<Response<void>>(`${environment.apiEndpoint}/parts/reorder-point-notification`, request)
    );
  }
}
